import { Component, inject, Input, OnChanges, OnInit, Output, SimpleChanges, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import moment from 'moment';
import { DatastoreService } from '../services';
import { ApiService } from '../services/jrni/api.service';
import { AvailabilityService } from '../services/jrni/availability.service';
import {
	FormBuilder,
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	UntypedFormControl,
	Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateSplitPipe } from '../pipes/translate-split.pipe';

export interface Question {
	/**
	 * Question ID.
	 * Will be used for control name.
	 */
	id: string;
	/** Question text to display */
	name: string;
	/**
	 * Type/format of the question.
	 */
	detailType: 'number' | 'select';
	/**
	 * If question is visible
	 */
	visible: boolean;
	/**
	 * The default value for the question answer.
	 */
	defaultValue?: string;
	/**
	 * The minimum value for the question answer.
	 */
	minimumValue?: number;
	/**
	 * The maximum value for the question answer.
	 */
	maximumValue?: number;
	/**
	 * Options for the question.
	 * Only used if the question type is 'select'.
	 */
	options?: { name: string }[];
	/**
	 * The price of the item related to the question, eg a marriage certificate.
	 */
	price?: number;
	/**
	 * An object of conditionals that decide when the question should be shown.
	 */
	conditionals?: Record<string, string | Record<string, string | number>>;
	/**
	 * Whether the question is required or not.
	 */
	required?: boolean;
	/**
	 * The answer to the question.
	 * Will be set on value change.
	 */
	answer?: string | number;
}

/**
 * Component to display an enhanced version of the CostBeakdownComponent along with
 * questions allowing you to add on items to the booking.
 */
@Component({
	selector: 'app-fees-and-make-it-yours-enhancements',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		TranslatePipe,
		NgSelectModule,
		TranslateSplitPipe,
	],
	templateUrl: './fees-and-make-it-yours-enhancements.component.html',
	styleUrl: './fees-and-make-it-yours-enhancements.component.scss',
})
export class FeesAndMakeItYoursEnhancementsComponent
	implements OnInit, OnChanges {
	@Input() costBreakDown: any;
	@Input() company: any;
	@Input() service: any;
	@Input() questions: Question[] = [];
	/** Used to decide whether to show validation errors */
	@Input() outerFormSubmitted = false;
	@Input() dataPolicy = 'Data Policy not set';
	@Input() adminJourney = false;

	@Output() questionChange: EventEmitter<Question> = new EventEmitter();

	ceremonyDateId: number;
	date: string;
	ceremonyDate: any;
	// fees: any = [];
	totalCost: any;
	daysAway = 0;
	costTotal = 0;
	depositTotal = 0;
  paymentType = 'full_balance';


  isCeremonyInFuture = false;
  /** A deposit only exists if /fees priced one; see calculateTotal(). */
  depositAvailable = false;

	formBuilder = inject(FormBuilder);

	form: FormGroup = this.formBuilder.group({
		data_policy: new UntypedFormControl('', Validators.requiredTrue),
    payment_type: new UntypedFormControl('full_balance', Validators.requiredTrue)
	});
	// add the questions to the form;

	constructor(
		private apiService: ApiService,
		public dataStore: DatastoreService,
		private availabilityService: AvailabilityService
	) { }

	ngOnInit(): void {
    console.log('Initialising FeesAndMakeItYoursEnhancementsComponent');
		if (!this.dataStore.makeItYoursPaymentType) {
			this.dataStore.makeItYoursPaymentType = this.paymentType;
		}
		this.form.valueChanges.subscribe((values) => this.onQuestionChange(values));

    console.log('Setting up subscription to addFees$', this.dataStore.serviceFees);
		this.dataStore.serviceFees = [];

		this.dataStore.addFees$.subscribe((feeToAdd) => {
			if (feeToAdd && typeof feeToAdd.quantity !== 'undefined') {
				this.dataStore.serviceFees = this.dataStore.serviceFees.filter(
					(item) => item.questionid !== feeToAdd.questionid
				);
        if (feeToAdd.quantity > 0) {
          this.dataStore.serviceFees.push({
            questionid: feeToAdd.questionid,
            item: feeToAdd.item,
            quantity: feeToAdd.quantity,
            cost: feeToAdd.cost,
            deposit: feeToAdd.cost,
          });
        }
				this.calculateTotal();
			}
		});

		if (!this.company) {
			this.company = this.dataStore.selectedDepartment;
		}

		if (!this.service) {
			this.service = this.dataStore.selectedService;
		}
		this.calculate().catch((error) => {
			// /fees could not price this ceremony (404 = missing fee row; reason
			// logged by the api service). Unpriced is not free — flag it so the
			// summary refuses checkout instead of charging £0.
			console.error('make-it-yours: unpriced — blocking online checkout', error);
			this.dataStore.feesUnavailable = true;
			this.dataStore.serviceFees = [];
		});
	}

	setMinAndMaxValue() {
		this.questions.forEach((question) => {
			if (
				question.detailType === 'select' &&
				this.checkIfNumber(question.options[0].name)
			) {
				question.minimumValue = Number(question.options[0].name);
				question.maximumValue = Number(
					question.options[question.options.length - 1].name
				);
			}
		});
	}

	checkIfNumber(input: unknown): boolean {
		const num = Number(input);
		return !isNaN(num) && isFinite(num);
	}

	async calculate() {
		this.dataStore.feesUnavailable = false;
		const slot = await this.availabilityService.getSelectedSlot();
		console.log(slot);
		if (slot) {
			this.date = moment(slot.start).format('YYYY-MM-DD HH:mm:ss');
			this.daysAway = moment(slot.start).diff(moment(), 'd');
		}
    console.log('daysAway', this.daysAway);
    if (this.daysAway > 90) {
      this.isCeremonyInFuture = true;
    }

		if (this.date) {
			let fee: any;
			if (this.dataStore.storedFee) {
				console.log("Using stored fee", this.dataStore.storedFee);
				fee = this.dataStore.storedFee;
			} else {
				fee = await this.apiService.fees(
					this.company.id,
					this.date,
					this.service.id,
					this.service.calculated_duration
				);
			}

			this.dataStore.serviceFees = this.dataStore.serviceFees.filter(
				(item) => item.questionid !== fee['questionid']
			);
      console.log('fees', this.dataStore.serviceFees);
			// this.dataStore.serviceFees.push({
			// 	questionid: fee['questionid'],
			// 	item: this.service.name,
			// 	quantity: 1,
			// 	cost: fee['full_fee'],
			// 	deposit: fee['deposit'],
			// });

			if (fee.short_notice > 0) {
				if (
					this.service.extra?.hasOwnProperty('fees_extra_item_short_notice_id')
				) {
					const extraItemShortNoticeId =
						this.service.extra.fees_extra_item_short_notice_id;
					const extraItem: any = await this.apiService.getExtraItem(
						this.company.id,
						this.service.id,
						extraItemShortNoticeId
					);
					this.dataStore.serviceFees.push({
						questionid: 'noid1',
						item: extraItem.item_name,
						quantity: 1,
						cost: extraItem.item_price,
						deposit: extraItem.item_price,
						item_id: extraItemShortNoticeId,
					});
				}
			}

			if (fee.out_of_hours > 0) {
				// get the day
				let extraItemOutOfHoursId: any = false;
				const dayOfWeek = moment(this.date).format('ddd');
				if (fee.is_bank_holiday || dayOfWeek === 'Sun') {
					if (
						this.service.extra?.hasOwnProperty(
							'fees_extra_item_out_of_hours_sun_bank_hol_id'
						)
					) {
						extraItemOutOfHoursId =
							this.service.extra.fees_extra_item_out_of_hours_sun_bank_hol_id;
					}
				} else {
					if (
						this.service.extra?.hasOwnProperty(
							'fees_extra_item_out_of_hours_mon_sat_id'
						)
					) {
						extraItemOutOfHoursId =
							this.service.extra.fees_extra_item_out_of_hours_mon_sat_id;
					}
				}
				if (extraItemOutOfHoursId) {
					const extraItem: any = await this.apiService.getExtraItem(
						this.company.id,
						this.service.id,
						extraItemOutOfHoursId
					);
					this.dataStore.serviceFees.push({
						questionid: 'noid2',
						item: extraItem.item_name,
						quantity: 1,
						cost: extraItem.item_price,
						deposit: extraItem.item_price,
						item_id: extraItemOutOfHoursId,
					});
				}
			}

      if (fee.ceremony_fee_vat_n > 0) {
        if (this.service.extra?.hasOwnProperty('fees_extra_item_id')) {
          const extraItemCeremonyFeeId =
						this.service.extra.fees_extra_item_id;
					const extraItem: any = await this.apiService.getExtraItem(
						this.company.id,
						this.service.id,
						extraItemCeremonyFeeId
					);
					this.dataStore.serviceFees.push({
						questionid: 'noid2',
						item: extraItem.item_name,
						quantity: 1,
						cost: fee.ceremony_fee_vat_n,
						deposit: fee.booking_fee,
						// The deposit a customer can pay today IS the booking fee, and it
						// rides on this line only. Flag it explicitly: 'noid2' is also used
						// by the out-of-hours line above, and it is load-bearing for the
						// n8n workers and verify-payment, so it cannot be renamed.
						depositEligible: true,
						item_id: extraItemCeremonyFeeId,
					});
        }
      }

      if (fee.booking_fee > 0) {
        if (this.service.extra?.hasOwnProperty('fees_extra_item_booking_fee_id')) {
          const extraItemBookingFeeId =
						this.service.extra.fees_extra_item_booking_fee_id;
					const extraItem: any = await this.apiService.getExtraItem(
						this.company.id,
						this.service.id,
						extraItemBookingFeeId
					);
					this.dataStore.serviceFees.push({
						questionid: 'noid3',
						item: extraItem.item_name,
						quantity: 1,
						cost: fee.booking_fee,
						deposit: fee.booking_fee,
						item_id: extraItemBookingFeeId,
					});
        }
      }

      if (fee.hire_fee_vat_s > 0) {
        if (this.service.extra?.hasOwnProperty('fees_extra_item_hire_fee_id')) {
          const extraItemHireFeeId =
						this.service.extra.fees_extra_item_hire_fee_id;
					const extraItem: any = await this.apiService.getExtraItem(
						this.company.id,
						this.service.id,
						extraItemHireFeeId
					);
					this.dataStore.serviceFees.push({
						questionid: 'noid4',
						item: extraItem.item_name,
						quantity: 1,
						cost: fee.hire_fee_vat_s,
						deposit: fee.hire_fee_vat_s,
						item_id: extraItemHireFeeId,
					});
        }
      }

      if (fee.certificate_fee > 0) {
        if (this.service.extra?.hasOwnProperty('fees_extra_item_certificate_included_id')) {
          const extraItemCertIncludedFeeId =
						this.service.extra.fees_extra_item_certificate_included_id;
					const extraItem: any = await this.apiService.getExtraItem(
						this.company.id,
						this.service.id,
						extraItemCertIncludedFeeId
					);
					this.dataStore.serviceFees.push({
						questionid: 'noid5',
						item: extraItem.item_name,
						quantity: 1,
						cost: extraItem.item_price,
						deposit: extraItem.item_price,
						item_id: extraItemCertIncludedFeeId,
					});
        }
      }
		}
    console.log('fees', this.dataStore.serviceFees);
		this.calculateTotal();
	}

	calculateTotal() {
		let costTotal = 0;
		let depositTotal = 0;
		costTotal = this.dataStore.serviceFees.reduce(
			(sum, fee) => sum + fee.cost * (fee.quantity ? fee.quantity : 0),
			0
		);
		// Sum the lines explicitly flagged deposit-eligible instead of taking
		// the first 'noid2' row. 'noid2' is shared by the ceremony-fee and
		// out-of-hours lines, and the sort below reorders serviceFees by cost on
		// every recalculation — so first-match-wins could hand back the
		// out-of-hours line's deposit instead of the ceremony line's.
		const eligibleDeposit = this.dataStore.serviceFees.reduce(
			(sum, fee) =>
				fee.depositEligible
					? sum + (Number(fee.deposit) || 0) * (Number(fee.quantity) || 0)
					: sum,
			0
		);
		// /fees answers booking_fee: 0 for a tenant with no configured deposit,
		// so there is nothing to take today. Offering "Booking Fee" then quoted
		// "you need to pay £0.00 today" and sent amount: 0 to the gateway, which
		// opens no payment page — a booking with no money behind it. Fall back to
		// the full balance and let the template hide the option.
		this.depositAvailable = eligibleDeposit > 0;
		if (!this.depositAvailable && this.paymentType === 'deposit') {
			this.paymentType = 'full_balance';
			this.dataStore.makeItYoursPaymentType = 'full_balance';
			this.form.patchValue(
				{ payment_type: 'full_balance' },
				{ emitEvent: false }
			);
		}
		// Every branch below charged the same full-balance reduce; it is costTotal.
		this.totalCost = costTotal;
		if (this.isCeremonyInFuture && this.paymentType === 'deposit') {
			depositTotal = eligibleDeposit;
		}
		this.dataStore.serviceFees = this.dataStore.serviceFees.sort(
			(a, b) => b.cost - a.cost
		);
		this.dataStore.totalCost = this.totalCost;
		this.costTotal = costTotal;
		this.depositTotal = depositTotal;
    if (this.isCeremonyInFuture) {
      if (this.paymentType === 'deposit') {
        this.dataStore.totalCost = depositTotal;
        this.totalCost = depositTotal;
      }
    }
		console.log('this.dataStore.totalCos', this.dataStore.totalCost, 'costTotal', costTotal, 'depositTotal', depositTotal);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['questions']) {
			this.updateFormControls(changes['questions'].currentValue);
		}
	}

	updateNumberInput(
		questionId: number,
		increase: boolean,
		minimum = 0,
		maximum = 20
	) {
		const ctrl = this.form.get(`${questionId}`);
		let currentValue = ctrl.value;
		if (typeof currentValue !== 'number') {
			currentValue = Number(currentValue);
		}
		if (increase) {
			if (currentValue <= maximum - 1) {
				ctrl.setValue(currentValue + 1);
			}
		} else {
			if (currentValue >= minimum + 1) {
				ctrl.setValue(currentValue - 1);
			}
		}
	}

	updateFormControls(updatedQuestions: Question[]) {
		this.setMinAndMaxValue();
		updatedQuestions.forEach((q) => {
			if (!this.form.get(`${q.id}`)) {
				this.form.addControl(
					q.id,
					new FormControl(
						q.answer || q.defaultValue || 0,
						[
							q.required ? Validators.required : null,
							q.minimumValue ? Validators.min(q.minimumValue) : null,
							q.maximumValue ? Validators.max(q.maximumValue) : null,
						].filter(Boolean)
					)
				);
			}
		});
	}

	/**
	 * Adds the form value to the answer property for a question change and emits that question
	 * @param formChanges
	 */
	onQuestionChange(formChanges: Record<string, string>) {
    console.log('changes', formChanges);
	const updatedPaymentType = this.paymentType !== formChanges.payment_type;
    this.paymentType = formChanges.payment_type;
	//recalculate total when changing payment types
	if (updatedPaymentType) {
		this.calculateTotal();
	}
	this.dataStore.makeItYoursPaymentType = this.paymentType;
	const changes: Question[] = [];
	for (const [id, value] of Object.entries(formChanges)) {
		const question: Question = this.questions[this.findQuestionIndex(id)];
		if (question && question.visible && question.answer !== value) {
			if (
				question.minimumValue !== undefined &&
				Number(value) < question.minimumValue
			) {
				this.form.get(id).setValue(question.minimumValue);
			} else if (
				question.maximumValue &&
				Number(value) > question.maximumValue
			) {
				this.form.get(id).setValue(question.maximumValue);
			} else {
				question.answer = value;
				changes.push(question);
			}
		}
	}

		for (const question of changes) {
			this.questionChange.emit(question);
		}
	}

	findQuestionIndex = (id: string): number =>
		this.questions.findIndex((question) => `${question.id}` === id);
}
